/**
 * Artifact canvas dispatcher — picks the per-kind renderer based on
 * `artifact.kind`. Each canvas is self-contained (motion wrapper, header,
 * body) so kind-specific behaviors don't leak across renderers.
 *
 * Mounts as a sibling of the chat layout (not a fixed overlay), so the
 * existing `<Messages>` and `<MultimodalInput>` stay visible on the left.
 *
 * @module components/artifacts/artifact
 */

import { effectiveArtifactKind } from "@/lib/artifacts/html-detect";
import { useArtifactStore } from "@/store/artifact-store";
import { DocumentCanvas } from "./document-canvas";
import { HtmlCanvas } from "./html-canvas";
import { ImageCanvas } from "./image-canvas";
import { PresentationCanvas } from "./presentation-canvas";
import { SpreadsheetCanvas } from "./spreadsheet-canvas";

/**
 * Top-level artifact canvas. Reads the active artifact's kind from the
 * store and routes to the matching per-kind canvas component. Mounts only
 * when an artifact is loaded (id non-empty); the parent `<AnimatePresence>`
 * in chat.tsx drives the visible/hidden transition.
 */
export function Artifact() {
  // Resolve inside the selector so we only re-render when the *effective* kind
  // flips, not on every streamed content chunk. HTML pages mis-tagged as
  // `document` render on the HTML canvas instead of as raw markdown text.
  const kind = useArtifactStore((s) =>
    effectiveArtifactKind(s.artifact.kind, s.artifact.content),
  );

  switch (kind) {
    case "html":
      return <HtmlCanvas />;
    case "presentation":
      return <PresentationCanvas />;
    case "image":
      return <ImageCanvas />;
    case "spreadsheet":
      return <SpreadsheetCanvas />;
    case "document":
    default:
      return <DocumentCanvas />;
  }
}
